// The auto-attendant ROWS of the "Calls go to" card, and the two modals behind them.
//
// NOT A CARD. Kevin, 2026-08-27: "the gear is gone. how do you set the route? If the route is ring
// group, the card should show specific configurable items for a ring group."
//
// I had it backwards. RouteTypeCard was already the "Calls go to" card — it owns the gear that sets
// the route, and it already carried ring-group specifics with a comment saying the group's settings
// live there rather than in a card of their own, because "a separate ring-group card repeated the
// same two facts one card down and gave the operator two gears for one decision — the duplication
// this page keeps regrowing." I built exactly that card, and suppressing RouteTypeCard to hide the
// duplication took the gear with it.
//
// So this file exports ROWS and MODALS. One card, one gear, rows that depend on the destination.
//
// Kevin, 2026-08-27: "clicking on the box in the diagram should open a modal form. simplify the copy.
// let's keep this simple and adopt common sense responses to things like invalid key press which
// should just restart the IVR. Remove the 'Someone calls' box. maybe this would look better as a
// table with nested tables/rows? combine the 'Calls go to' with the card that is specific to the
// route. The goal here is to not overwhelm the operator with so many options that they need
// training."
//
// FOUR THINGS THAT SHAPED THIS, all of them subtractions:
//
//  1. A TABLE, NOT BOXES. Floating nodes and connectors take a lot of screen to say what four rows
//     say, and "step, then what happens" is a table's native shape. Branches are nested rows under
//     their key rather than a second diagram.
//  2. THE FOUR ERROR PROMPTS ARE GONE FROM THE UI. They read identically on every menu we have, and
//     a wrong key restarting the menu is the only sensible answer anyone was going to pick. So it is
//     the behaviour, not a question — the endpoint still exists for the day somebody needs it.
//  3. NO "SOMEONE CALLS" BOX. The card says what calls to this number reach; a box saying a call
//     arrives was restating the heading.
//  4. ONE CARD, NOT TWO. This IS the "Calls go to" card for a line that answers with a menu — the
//     separate one-liner said less than this card's own first row.
//
// WHAT AN OPERATOR CAN DO HERE: change the greeting, build a menu where there is none, and set what
// the keys do.
//
// KEYS WERE READ-ONLY UNTIL 2026-09-01, and the reason was the second half of this sentence: "no
// route writes them on an existing menu." There genuinely was none — keys could only be set when
// the menu was created, and the Add-a-menu wizard deliberately creates one with none, so there was
// no path from "menu created" to "menu works". Rebuilding Lyons Communications ran straight into
// it. `PUT /lines/:id/ivr/keys` exists now, so the constraint is gone with it.
//
// THE MODAL EDITS THE WHOLE MAP, and that is the endpoint's shape rather than a UI shortcut:
// `PUT /provision/ivr/{id}/entries` REPLACES WHOLESALE, so sending "the keys the operator just
// touched" deletes the rest. Editing every key in one form is the honest rendering of that — a
// per-row save would look like a partial update and could not be one.
//
// The other half of the old caution still stands and is why the destination is a PICKER: a key
// pointed at something that does not exist strands callers with nothing logged.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import FormModal from '../shared/FormModal.jsx';
import RecordingPickerModal from '../shared/RecordingPickerModal.jsx';

/**
 * One row: what it is, what it does, and the action that changes it.
 *
 * Kevin, 2026-08-27: "Each row should look like a table row with a kabab menu or an action button(s)
 * at the end of the row. indentation should be used to show relationships."
 *
 * A BUTTON, NOT A KEBAB, while there is one action. A kebab is a menu, and a menu holding a single
 * item costs a click to learn that. When a row grows a second action it should become one.
 */
export function Row({ label, children, nested = false, muted = false, action = null }) {
  return (
    <tr className="border-t border-gray-100 first:border-t-0">
      <td className={`w-44 py-1.5 pr-3 align-top text-xs ${nested ? 'pl-6' : ''} ${muted ? 'text-gray-400' : 'text-gray-500'}`}>
        {label}
      </td>
      <td className="py-1.5 text-xs text-gray-900">{children}</td>
      <td className="w-20 py-1.5 pl-2 text-right align-top">
        {action && (
          <button type="button" onClick={action.onClick}
            className="rounded border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
            {action.label}
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * The greeting, through the shared recording picker.
 *
 * THE PICKER IS NOT OURS. It lives in components/shared/RecordingPickerModal.jsx because a greeting
 * is only the first thing that needs a recording chosen — voicemail slots and announcements want the
 * same modal, and Kevin asked for it to be shareable rather than have this file grow a second copy
 * later. All this wrapper does is say what the recording is FOR and what to do with the choice.
 */
function GreetingModal({ line, hostRecordings, onClose }) {
  // Kept ONLY to say how many the phone system holds that are not in this customer's library. Not
  // silent: an operator who expects to see something should be told why it is absent, and "there
  // are none" would be a different and untrue statement.
  const withheld = (hostRecordings || []).length;
  const qc = useQueryClient();
  const subscriberId = line.subscriberId ?? line.subscriber_id;

  // THE CUSTOMER'S OWN RECORDINGS, so an upload is still there tomorrow.
  //
  // Kevin, 2026-08-27: "I don't see them in the drop down?!" — because the picker was listing only
  // the phone system's catalog plus whatever had been uploaded during that one modal session. Three
  // recordings saved to this customer, and none of them offered the next time the modal opened. The
  // just-uploaded one appeared because the picker held it in component state; reloading the page
  // threw it away.
  const { data: lib } = useQuery({
    queryKey: ['subscriber', String(subscriberId), 'recordings'],
    queryFn: () => api.get(`/subscribers/${subscriberId}/recordings`),
    enabled: subscriberId != null,
    retry: false,
  });
  // TWO ID SPACES. A recording already on the phone system goes in `pbx_recording_id`; one from the
  // customer's library — including one just uploaded — goes in `recording_id`. Sending the wrong
  // field points a menu at unrelated audio, so the option's own `source` decides.
  const call = (opt, apply) => api.post(`/lines/${line.id}/ivr/prompt`, {
    slot: 'announcement',
    ...(opt.source === 'library' ? { recording_id: opt.id } : { pbx_recording_id: opt.id }),
    ...(apply ? { apply: true } : {}),
  });

  return (
    <RecordingPickerModal
      // IT PICKS A RECORDING. Kevin, twice: "The modal card also says greeting, when it is Recording
      // selector" → "still says greetings". The context belongs in the subtitle, where it says which
      // slot is being filled without renaming the thing the modal does.
      title="Choose a recording"
      subtitle={withheld
        ? `For the greeting — what every caller hears first. ${withheld} on the phone system `
          + 'are not shown: they are not in this customer’s library, so they may be another '
          + 'customer’s. Upload from the Recordings tab to add one.'
        : 'For the greeting — what every caller hears first'}
      // THIS CUSTOMER'S RECORDINGS, AND ONLY THEIRS.
      //
      // Kevin, 2026-09-02: "I shouldn't see SML recordings or IVRs, etc… when I am configuring Lyons
      // Communications line." The list used to merge the customer's library with the PHONE SYSTEM'S
      // WHOLE CATALOGUE, which is host-wide and unowned — pbx-02's held Stone Mountain Lodge's
      // greeting, and picking it would have put one customer's audio into another's menu. The proxy
      // route that serves it says as much: "an operator view — it must not be handed to a subscriber
      // as their library."
      //
      // The host half is DROPPED rather than filtered because a library item does not carry the id
      // of its pushed copy, so there is nothing to filter on. Little is lost: selecting a library
      // recording pushes it to the host, so anything the customer owns stays reachable. What becomes
      // unreachable is audio existing only on the host and in no customer's library — precisely the
      // audio we cannot say is theirs.
      //
      // `multi_part` recordings cannot be swapped in one piece, so they stay offered-but-disabled —
      // an operator looking for one should see it and see why not.
      recordings={lib == null ? null : (lib.items || []).map((r) => ({
        id: r.id, name: r.name, source: 'library', disabled: r.multi_part,
      }))}
      subscriberId={subscriberId}
      kind="ivr_prompt"
      // The preview is still run — it is where the server's refusals come from, and the card above
      // already shows what callers hear today, so it does not need to be a second button.
      onPreview={(opt) => call(opt, false)}
      onSelect={async (opt) => {
        const r = await call(opt, true);
        if (r?.error) throw Object.assign(new Error(r.error), { body: r });
        qc.invalidateQueries({ queryKey: ['line', String(line.id), 'route-target'] });
      }}
      onClose={onClose}
    />
  );
}

/** Build the menu. Defaults are the server's, so nothing sensible has to be chosen here. */
function AddMenuModal({ line, onClose }) {
  const [name, setName] = useState('');
  const [preview, setPreview] = useState(null);
  const qc = useQueryClient();

  const call = useMutation({
    mutationFn: (extra) => api.post(`/lines/${line.id}/ivr`, { name: name.trim(), keys: [], ...extra }),
  });

  const check = async () => {
    const r = await call.mutateAsync({}).catch((e) => ({ error: e?.body?.error || e?.message }));
    setPreview(r);
  };
  const apply = async () => {
    const r = await call.mutateAsync({ apply: true }).catch((e) => ({ error: e?.body?.error || e?.message }));
    if (r?.error) { setPreview(r); return; }
    qc.invalidateQueries({ queryKey: ['line', String(line.id), 'route-target'] });
    onClose();
  };

  const ok = preview && !preview.error && !preview.blockers?.length;
  return (
    <FormModal title="Add a menu" subtitle="Callers hear a greeting, then press a key" onClose={onClose}
      footer={ok ? (
        <button type="button" onClick={apply} disabled={call.isPending}
          className="rounded bg-red-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40">
          {call.isPending ? 'Building…' : 'Build it'}
        </button>
      ) : (
        <button type="button" onClick={check} disabled={!name.trim() || call.isPending}
          className="rounded bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40">
          {call.isPending ? 'Checking…' : 'Continue'}
        </button>
      )}>
      <label className="block text-sm font-medium text-gray-900" htmlFor="menu-name">Name</label>
      <input id="menu-name" value={name} maxLength={50} onChange={(e) => setName(e.target.value)}
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
      {/* The sensible defaults are applied, not offered — one line, not three. */}
      <p className="mt-1 text-xs text-gray-500">
        Callers who press nothing go to voicemail. A wrong key replays the menu.
      </p>
      {preview && (
        <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-2">
          {preview.error || preview.blockers?.length ? (
            <ul className="list-disc space-y-0.5 pl-4 text-xs text-red-700">
              {(preview.blockers?.length ? preview.blockers : [{ why: preview.error }])
                .map((b, i) => <li key={i}>{b.why}</li>)}
            </ul>
          ) : (
            <>
              <ul className="space-y-0.5">
                {(preview.steps || []).map((s) => (
                  <li key={s.id} className="text-xs text-gray-600">• {s.description}</li>
                ))}
              </ul>
              {preview.note && <p className="mt-1 text-xs text-amber-800">{preview.note}</p>}
            </>
          )}
        </div>
      )}
    </FormModal>
  );
}

/**
 * The rows for a line that answers with a menu — or the one row that offers to build one.
 *
 * `onEdit` is called with a modal name rather than opening it here: a modal rendered inside a
 * `<tbody>` is invalid HTML, so the card owns them and these rows only ask.
 */
// Picker sentinel for "build me a new menu". Never sent as a destination — the row carries
// `submenu` instead, because the child has no id until the server makes it.
const NEW_MENU = '__new_menu__';

/**
 * What the keys do — the WHOLE map, in one form.
 *
 * `PUT /provision/ivr/{id}/entries` replaces wholesale, so this is not a UI shortcut: sending only
 * the rows an operator touched would delete every key they did not. The endpoint takes the complete
 * set and this form is the honest rendering of that contract.
 *
 * THE DESTINATION IS A PICKER, never free text. A key pointed at something that does not exist
 * strands callers on a digit with nothing logged — the caution that kept these read-only in the
 * first place, and the half of it that is still true.
 */
export function KeysModal({ line, data, onClose }) {
  const qc = useQueryClient();
  const object = data?.object;

  // The line's own extensions, and the other menus this host has. Both are reads the card already
  // makes elsewhere, shared by query key rather than fetched twice.
  const { data: exts } = useQuery({
    queryKey: ['line', String(line.id), 'extensions'],
    queryFn: () => api.get(`/lines/${line.id}/extensions`),
    retry: false,
  });
  const { data: options } = useQuery({
    queryKey: ['line', String(line.id), 'route-options'],
    queryFn: () => api.get(`/lines/${line.id}/route-options`),
    retry: false,
  });

  const mailbox = (exts?.extensions || exts || [])[0]?.username || null;
  const choices = [
    ...(exts?.extensions || exts || []).map((e) => ({
      value: `from-did-direct,${e.username},1`,
      label: `Ring ${e.username}${e.displayName ? ` — ${e.displayName}` : ''}`,
    })),
    ...(mailbox ? [{ value: `ext-local,vmu${mailbox},1`, label: 'Go to voicemail' }] : []),
    // Another menu on this host — the two-level route, from the UI.
    // Another menu on this host — the two-level route.
    //
    // `label` is what the endpoint returns; `name` was a guess and fell through to the bare id, so
    // the picker read “Open the menu “5”” for a menu called SMLMainMenu.
    //
    // AND WHOSE IT IS, because the host's catalogue is host-wide: without this the picker offers
    // one customer's attendant as a destination for another's, and the operator cannot tell.
    ...((options?.ivrs || [])
      .filter((m) => String(m.id) !== String(object?.id))
      .map((m) => ({
        value: `ivr-${m.id},s,1`,
        label: `Open the menu “${m.label || m.name || m.id}”`
          + (m.belongs_to_another_line ? ' — another customer’s' : ''),
      }))),
    { value: 'app-blackhole,hangup,1', label: 'Hang up' },
    // THE SECOND LEVEL. Chosen here, named below, built by the server child-first — a menu nothing
    // points at is unreachable, so the child exists safely before this menu's keys name it.
    { value: NEW_MENU, label: 'Open a new menu…' },
  ];

  // A row is either a destination or a NEW MENU. `newMenu` holds the name while it is being typed;
  // the key has no `ivr-<id>` until the server builds it, which is why the two cannot share a
  // field. NEW_MENU is a sentinel in the picker, never a destination sent anywhere.
  const [rows, setRows] = useState(() =>
    (object?.entries || []).map((e) => ({ selection: String(e.selection), destination: e.destination })));
  const [error, setError] = useState(null);

  const free = '0123456789*#'.split('').filter((d) => !rows.some((r) => r.selection === d));

  const save = useMutation({
    // A new-menu row sends `submenu` INSTEAD of a destination, which is the shape the endpoint
    // takes: it creates the child, then writes the whole key map with the child's real id in it.
    mutationFn: () => api.put(`/lines/${line.id}/ivr/keys`, {
      keys: rows.map((r) => (r.destination === NEW_MENU
        ? { selection: r.selection, submenu: { name: (r.newMenu || '').trim() } }
        : { selection: r.selection, destination: r.destination })),
      apply: true,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['line', String(line.id), 'route-target'] });
      qc.invalidateQueries({ queryKey: ['line', String(line.id), 'route-options'] });
      onClose();
    },
    onError: (e) => setError(e?.message || 'The keys could not be saved.'),
  });

  const dup = new Set(rows.map((r) => r.selection)).size !== rows.length;
  // A new-menu row is incomplete until it has a NAME — saving one without would create a menu
  // nobody can identify on the host.
  const incomplete = rows.some((r) => !r.selection || !r.destination
    || (r.destination === NEW_MENU && !(r.newMenu || '').trim()));

  return (
    <FormModal
      title="What the keys do"
      subtitle="Every key this menu will have"
      onClose={onClose}
      footer={(
        <button type="button" onClick={() => { setError(null); save.mutate(); }}
          disabled={save.isPending || dup || incomplete}
          className="rounded bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40">
          {save.isPending ? 'Saving…' : 'Save keys'}
        </button>
      )}>
      <p className="mb-3 text-xs text-gray-500">
        These are all the keys the menu will have. Anything removed here stops working when you save.
      </p>

      {rows.map((r, i) => (
        <div key={i} className="mb-2 flex items-center gap-2">
          <select
            value={r.selection}
            onChange={(ev) => setRows(rows.map((x, n) => (n === i ? { ...x, selection: ev.target.value } : x)))}
            className="w-20 rounded border border-gray-300 px-2 py-1 text-xs"
          >
            <option value={r.selection}>{r.selection}</option>
            {free.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select
            value={r.destination}
            onChange={(ev) => setRows(rows.map((x, n) => (n === i ? { ...x, destination: ev.target.value } : x)))}
            className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
          >
            {/* A destination the picker does not know is kept and shown, never silently dropped:
                it is something somebody built on the PBX and saving must not quietly retarget it. */}
            {!choices.some((c) => c.value === r.destination) && r.destination && (
              <option value={r.destination}>{r.destination} (set on the PBX)</option>
            )}
            {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <button type="button" onClick={() => setRows(rows.filter((_, n) => n !== i))}
            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">
            Remove
          </button>
        </div>
      ))}

      {/* The name for each new menu, asked only where one was chosen. */}
      {rows.map((r, i) => (r.destination === NEW_MENU ? (
        <div key={`name-${i}`} className="mb-2 flex items-center gap-2 pl-20">
          <label className="text-xs text-gray-500" htmlFor={`newmenu-${i}`}>Name the menu for key {r.selection}</label>
          <input id={`newmenu-${i}`} value={r.newMenu || ''}
            onChange={(ev) => setRows(rows.map((x, n) => (n === i ? { ...x, newMenu: ev.target.value } : x)))}
            placeholder="After hours"
            className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs" />
        </div>
      ) : null))}

      {!rows.length && <p className="mb-2 text-xs text-gray-500">No keys. Callers will hear the greeting and fall through.</p>}

      {free.length > 0 && (
        <button type="button"
          onClick={() => setRows([...rows, { selection: free[0], destination: choices[0]?.value || '' }])}
          className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
          Add a key
        </button>
      )}

      {dup && <p className="mt-2 text-xs text-red-700">Two rows use the same key.</p>}
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
    </FormModal>
  );
}

/**
 * One key, and everything under it.
 *
 * RECURSIVE, because a call tree is. Depth is expressed by indentation and nothing else — there is
 * no level number here, the same way there is none in route_links — so a third level costs this
 * component nothing.
 *
 * WHAT EACH LINE OF NESTING IS FOR:
 *   a menu   its own keys and fall-throughs, indented under the key that opens it
 *   repeats  a key pointing back at a menu already shown ("press * to hear this again"). Said, not
 *            expanded, or the render would recurse as far as the data let it.
 *   gone     a key pointing at a menu the host does not have. pbx-02 carries four of these; the
 *            caller is dropped into a context Asterisk cannot find with NOTHING logged, so this is
 *            the one row here that is red.
 */
function KeyRows({ node, depth, action = null }) {
  const to = node.goes_to;
  const isMenu = to?.kind === 'ivr';
  const gone = isMenu && to.exists === false;

  const label = (() => {
    if (!to) return 'nowhere';
    if (gone) return `a menu that no longer exists (${to.id})`;
    if (isMenu) return `opens “${to.name || to.id}”`;
    return to.label || to.destination || 'elsewhere';
  })();

  return (
    <>
      <Row label={`Press ${node.selection}`} nested={depth > 0} action={action}>
        <span className={gone ? 'font-medium text-red-700' : ''}>{label}</span>
        {isMenu && to.belongs_to_another_line && (
          <span className="ml-2 text-gray-500">— another customer’s menu</span>
        )}
        {to?.repeats && <span className="ml-2 text-gray-500">— back to this menu</span>}
        {to?.kind === 'extension' && (
          <div className="pl-4 text-gray-500">↳ no answer → voicemail</div>
        )}
      </Row>

      {/* The submenu's own rows, one level in. Not rendered for a repeat or a menu that is gone —
          there is nothing to show and pretending otherwise would invent a flow. */}
      {isMenu && !gone && !to.repeats && (
        <>
          {(to.keys || []).map((k, i) => <KeyRows key={`${k.selection}-${i}`} node={k} depth={depth + 1} />)}
          {!(to.keys || []).length && (
            <Row label="Keys" nested muted>None yet — callers hear the greeting and fall through</Row>
          )}
          {to.no_key_pressed && (
            <Row label="No key pressed" nested>
              {to.timeout_seconds != null ? `Waits ${to.timeout_seconds}s, ` : ''}
              {(to.no_key_pressed.label || '').toLowerCase() || 'falls through'}
            </Row>
          )}
        </>
      )}
    </>
  );
}

export function AttendantRows({ data, onEdit, editable, tree = null }) {
  const object = data?.object;
  const hasMenu = data?.kind === 'ivr';
  const greeting = (object?.all_slots || []).find((p) => p.slot === 'announcement');
  const entries = object?.entries || [];
  // Only when the tree is for THIS menu — the card can render before both queries settle, and
  // pairing one menu's rows with another's keys would be worse than waiting.
  const treeKeys = (tree?.entry?.id && String(tree.entry.id) === String(data?.object_id))
    ? tree.entry.keys : null;

  const greetingName = (() => {
    if (!greeting || greeting.mode === 'unset') return 'not set';
    if (greeting.uses_standard) return 'the standard wording';
    if (greeting.silent) return 'nothing';
    if (greeting.mode === 'recording' && greeting.recording_id != null && !greeting.name) {
      return 'missing — callers hear silence';
    }
    return greeting.name || `recording ${greeting.recording_id}`;
  })();
  const broken = greeting?.mode === 'recording' && greeting.recording_id != null && !greeting.name;

  if (!hasMenu) {
    // "NO MENU" AND "WE COULD NOT LOOK" ARE DIFFERENT ANSWERS, and this told them apart only by
    // accident: `kind` is absent in both cases, so an unreadable host offered "Add". Pressing it
    // posts to the create-AND-point endpoint, which would build a second menu and aim the number
    // at it — abandoning an attendant that may well be answering calls right now. The read is the
    // only thing standing between that button and a live call flow, so it has to be confirmed
    // (2026-08-27).
    const readable = data?.verified === 'confirmed';
    return (
      <Row label="Menu" action={editable && readable ? { label: 'Add', onClick: () => onEdit('add') } : null}>
        <span className="text-gray-500">
          {readable ? 'None' : 'Could not read this from the phone system just now'}
        </span>
      </Row>
    );
  }

  return (
    <>
      <Row label="Greeting" action={editable ? { label: 'Change', onClick: () => onEdit('greeting') } : null}>
        <span className={broken ? 'font-medium text-red-700' : ''}>{greetingName}</span>
      </Row>

      {/* ONE ACTION FOR THE WHOLE MAP, on the first key row, because the write replaces wholesale.
          A Change button per row would promise a partial save the endpoint cannot make. */}
      {/* NESTED WHEN WE HAVE THE TREE. `route-target` answers one level, so a key that opens another
          menu used to render as a bare destination string with its own keys nowhere — an operator
          could build a second level and not see it. `route-tree` reaches all of them; without it
          this falls back to the flat entries, which is still right for a one-level menu and is what
          every line on the fleet but one has. */}
      {treeKeys
        ? treeKeys.map((k, i) => (
          <KeyRows key={`${k.selection}-${i}`} node={k} depth={0}
            action={i === 0 && editable ? { label: 'Change', onClick: () => onEdit('keys') } : null} />
        ))
        : entries.map((e, i) => (
          <Row key={`${e.selection ?? e.digit ?? i}`} label={`Press ${e.selection ?? e.digit ?? '?'}`}
            action={i === 0 && editable ? { label: 'Change', onClick: () => onEdit('keys') } : null}>
            {e.destinationLabel || e.destination || 'elsewhere'}
            {/* INDENTED, because it is what happens after that key rather than a step of its own. */}
            {e.destinationKind === 'extension' && (
              <div className="pl-4 text-gray-500">↳ no answer → voicemail</div>
            )}
          </Row>
        ))}

      {!entries.length && !treeKeys?.length && (
        <Row label="Keys" muted action={editable ? { label: 'Set keys', onClick: () => onEdit('keys') } : null}>
          None yet
        </Row>
      )}

      <Row label="No key pressed">
        {object?.timeout_seconds != null ? `Waits ${object.timeout_seconds}s, ` : ''}
        {object?.timeout_fallthrough?.set
          ? object.timeout_fallthrough.label.toLowerCase()
          : 'then repeats the menu'}
      </Row>
    </>
  );
}

export { GreetingModal, AddMenuModal };
